import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import BoxHolder from '../../components/shared/BoxHolder/BoxHolder';
import BtButton from '../bt-button/bt-button';
import { RowsCap, SupplierOverviewRow } from '../../services/service-evaluations';
import { WizardSeed, registerProfileUrl } from '../../util/evaluationsNavigation';
import SupplierEvaluationSummary from '../app-supplier-database/SupplierEvaluationSummary';
import RequestLine, { requestContextLabel } from './RequestLine';
import UnevaluatedLine, { seedSupplier } from './UnevaluatedLine';
import styles from './app-evaluation-overview.module.scss';

type Props = {
  row: SupplierOverviewRow;
  canWrite: boolean;
  onStart: (seed: WizardSeed) => void;
  onAnswerHidden: () => void;
};

const SupplierOverviewPanel: FC<Props> = ({ row, canWrite, onStart, onAnswerHidden }) => {
  const [t] = useTranslation('evaluations');
  const history = useHistory();
  const supplier = { _id: row.supplierId, name: row.name };

  const capNote = (cap: RowsCap | null) =>
    cap && (
      <p className={`${styles.capNote} ${styles.small} ${styles.muted}`}>
        {t('overview.supplier.capped', 'Visar {{shown}} av {{total}} utvärderingar.', {
          shown: cap.shown,
          total: cap.total,
        })}
      </p>
    );

  const openProfile = () => history.push(registerProfileUrl(row.supplierId));

  return (
    <div className={styles.panel}>
      <SupplierEvaluationSummary
        supplierId={row.supplierId}
        readOnly={false}
        onAnswerHidden={onAnswerHidden}
      />
      <BoxHolder className={styles.block}>
        <div className={styles.blockHead}>
          <div className={styles.blockActions}>
            {canWrite && !row.isDeleted && (
              <BtButton
                color="white"
                size="xxs"
                tabIndex={0}
                onClick={() => onStart({ project: null, suppliers: [supplier] })}
              >
                {t('register.newEvaluation', 'Ny utvärdering')}
              </BtButton>
            )}
            {/* The register cannot show a deleted supplier. */}
            {!row.isDeleted && (
              <BtButton color="white" size="xxs" tabIndex={0} onClick={openProfile}>
                {t('status.supplierProfile', 'Leverantörsprofil')}
              </BtButton>
            )}
          </div>
        </div>
        {row.ongoing.length > 0 && (
          <>
            <div className={styles.sectionHead}>{t('overview.supplier.ongoing', 'Pågående')}</div>
            {row.ongoing.map(request => (
              <RequestLine
                key={request.requestId}
                row={request}
                contextLabel={requestContextLabel(t, request)}
              />
            ))}
            {capNote(row.rowsCapped.ongoing)}
          </>
        )}
        {row.unevaluated.length > 0 && (
          <>
            <div className={styles.sectionHead}>
              {t('overview.supplier.wins', 'Ej utvärderade')}
            </div>
            {row.unevaluated.map(win => (
              <UnevaluatedLine
                key={win.projectId}
                title={win.projectName}
                sub={t('overview.panel.purchases', 'Inköp: {{names}}', {
                  names: win.purchases.map(purchase => purchase.name).join(', '),
                })}
                purchases={win.purchases}
                onEvaluate={
                  canWrite && win.canStart
                    ? () =>
                        onStart({
                          project: {
                            _id: win.projectId,
                            name: win.projectName,
                            number: win.projectNumber,
                          },
                          suppliers: [seedSupplier(supplier, win.purchases)],
                        })
                    : undefined
                }
              />
            ))}
          </>
        )}
        {row.finished.length > 0 && (
          <>
            <div className={styles.sectionHead}>{t('overview.supplier.finished', 'Avslutade')}</div>
            {row.finished.map(request => (
              <RequestLine
                key={request.requestId}
                row={request}
                contextLabel={requestContextLabel(t, request)}
              />
            ))}
            {capNote(row.rowsCapped.finished)}
          </>
        )}
      </BoxHolder>
    </div>
  );
};

export default SupplierOverviewPanel;
